import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { waitlistService } from '../../../services/waitlistService';

const WaitlistSignup = ({ onSuccess }) => {
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    familySize: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e?.target || {};
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    // Validation
    if (!formData?.email || !formData?.firstName) {
      setError('Please fill in all required fields');
      return;
    }

    if (!validateEmail(formData?.email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Add metadata for tracking
      const signupData = {
        ...formData,
        referralSource: 'website',
        metadata: {
          userAgent: navigator?.userAgent || '',
          timestamp: new Date()?.toISOString(),
          page: 'homepage',
          debug: process.env?.NODE_ENV === 'development'
        }
      };

      console.log('📝 Starting waitlist signup process:', {
        email: signupData?.email,
        firstName: signupData?.firstName,
        familySize: signupData?.familySize
      });

      // Add signup to database
      const { data: signupResult, error: signupError } = await waitlistService?.addSignup(signupData);
      
      if (signupError) {
        console.error('❌ Database signup failed:', signupError);
        throw signupError;
      }

      console.log('✅ Database signup successful:', signupResult?.id);

      // Send welcome email to user
      console.log('📧 Attempting to send welcome email...');
      const { data: emailData, error: emailError } = await waitlistService?.sendWelcomeEmail(signupData);
      
      if (emailError) {
        console.warn('⚠️ Welcome email failed to send:', emailError);
        // Store email failure info for debugging
        console.log('🔧 Email error details:', {
          error: emailError,
          recipient: signupData?.email,
          timestamp: new Date()?.toISOString()
        });
        // Don't fail the whole signup for email issues, but show a warning
        setError(`Signup successful, but there was an issue sending your welcome email. Please check your spam folder or contact support if you don't receive it within 10 minutes.`);
      } else {
        console.log('✅ Welcome email sent successfully:', emailData?.messageId);
      }

      // Notify admin (don't fail signup if this fails)
      console.log('🔔 Attempting to send admin notification...');
      waitlistService?.notifyAdmin(signupData)?.then(result => {
        if (result?.error) {
          console.warn('⚠️ Admin notification failed:', result?.error);
        } else {
          console.log('✅ Admin notification sent:', result?.data?.messageId);
        }
      })?.catch(err => {
        console.warn('🚨 Admin notification error:', err);
      });

      // Show success even if email partially failed
      setIsSuccess(true);
      onSuccess?.(signupData);
      
      // Reset form after success
      setTimeout(() => {
        setFormData({ email: '', firstName: '', familySize: '' });
        setIsSuccess(false);
        setError('');
      }, 8000); // Increased timeout for better UX
      
    } catch (err) {
      console.error('🚨 Signup process failed:', err);
      const errorMessage = err?.message || 'Something went wrong. Please try again.';
      setError(errorMessage);
      
      // Log detailed error for debugging
      console.log('🔍 Error details:', {
        error: err,
        formData: signupData,
        timestamp: new Date()?.toISOString()
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="bg-success/15 border border-success/30 rounded-2xl p-4 sm:p-6 text-center animate-in fade-in duration-500 max-w-full">
        <div className="w-16 h-16 bg-success/20 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
          <Icon name="Check" size={24} className="text-success" />
        </div>
        <h3 className="text-lg sm:text-xl font-heading font-bold text-success mb-2">
          Welcome to the Family! 🎉
        </h3>
        <p className="text-success/80 text-sm mb-3">
          You're on the list! We'll email you the moment Cynizee is ready to transform your family life.
        </p>
        <p className="text-success/70 text-xs mb-3">
          📧 Check your email for a welcome message with more details!
        </p>
        {error && (
          <div className="mt-3 p-2 bg-warning/10 border border-warning/20 rounded text-warning text-xs">
            <strong>Note:</strong> {error}
          </div>
        )}
        {/* Debug info for development */}
        {process.env?.NODE_ENV === 'development' && (
          <button
            onClick={() => {
              console.log('🧪 Testing email system...');
              waitlistService?.testEmailSystem()?.then(result => {
                console.log('🔍 Email test results:', result);
              });
            }}
            className="mt-3 text-xs text-success/60 hover:text-success underline"
          >
            🧪 Test Email System (Dev Only)
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="w-full max-w-full overflow-hidden">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-4">
          <Input
            type="email"
            name="email"
            placeholder="your@email.com"
            value={formData?.email}
            onChange={handleInputChange}
            label="Email Address"
            required
            className="text-center text-base sm:text-lg py-3 w-full"
          />
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              type="text"
              name="firstName"
              placeholder="Your name"
              value={formData?.firstName}
              onChange={handleInputChange}
              label="First Name"
              required
              className="w-full"
            />
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-white">
                Family Size (Optional)
              </label>
              <select
                name="familySize"
                value={formData?.familySize}
                onChange={handleInputChange}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">Select size</option>
                <option value="2">2 members</option>
                <option value="3">3 members</option>
                <option value="4">4 members</option>
                <option value="5">5 members</option>
                <option value="6+">6+ members</option>
              </select>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-destructive/15 border border-destructive/30 rounded-lg p-3 text-center animate-in fade-in duration-300 max-w-full">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 mb-2">
              <Icon name="AlertCircle" size={16} className="text-destructive flex-shrink-0" />
              <p className="text-destructive text-sm font-medium break-words">{error}</p>
            </div>
            <button
              type="button"
              onClick={() => navigator?.clipboard?.writeText(error)}
              className="text-xs text-destructive/70 hover:text-destructive underline"
            >
              Copy error message
            </button>
          </div>
        )}

        <Button 
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-heading font-bold py-4 text-sm sm:text-base lg:text-lg rounded-2xl transition-all duration-300 hover:scale-105 hover:shadow-lg min-h-[50px]"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <Icon name="Loader2" size={20} className="animate-spin flex-shrink-0" />
              <span className="hidden xs:inline">Securing Your Spot...</span>
              <span className="xs:hidden">Joining...</span>
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              <Icon name="Gift" size={20} className="flex-shrink-0" />
              <span className="hidden sm:inline">Sign up for Early Access + Special Launch Pricing!</span>
              <span className="sm:hidden">Join Early Access</span>
              <span className="text-base sm:text-lg">🎁</span>
            </span>
          )}
        </Button>

        <div className="text-center px-2">
          <p className="text-xs text-text-secondary">
            🔒 We respect your privacy. No spam, just family-friendly updates!
          </p>
        </div>
      </form>
    </div>
  );
};

export default WaitlistSignup;